import type { CaseManifestV1 } from "@cold-case/shared/case-manifest";
import type { AccusationPhaseState } from "@cold-case/shared/accusation-phase";
import { allPicksMatch } from "@cold-case/shared/accusation-phase";

import {
  arrestDynamicStatus,
  arrestInstructionsHeader,
  arrestStatusTone,
  arrestTabIntro,
  warrantDisabledReason,
  type ArrestStatusInput,
} from "../content/accusation-phase-copy.js";
import { mergeAccusationPhaseFromSync } from "./accusation-phase-merge.js";
import { renderCaseBriefHtml } from "../lib/case-brief-html.js";
import { npcPersonalityText } from "../lib/npc-case-notes.js";
import {
  clueDisplayName,
  npcDisplayName,
  relatedNpcDisplayName,
  resolveFoundAtDisplay,
} from "../lib/manifest-display.js";
import { npcSouthPortraitImgHtml } from "../lib/npc-portrait-html.js";
import { setPlayerOverlayOpen } from "../lib/player-input-gate.js";

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const TABS = ["Case & Clues", "Suspects", "Make an Arrest"] as const;

export type AccusationArrestContext = {
  manifest: CaseManifestV1;
  journal: Record<string, unknown>;
  playerNames: string[];
  players: { userId: string; displayName: string; characterNpcId: string }[];
  selfUserId: string;
  zoneName: (z: "downtown" | "maple_heights") => string;
  minEvidence: number;
};

export type AccusationArrestHandlers = {
  onPick: (killerInstanceId: string) => void;
  onAdopt: (fromUserId: string) => void;
  onWarrant: () => void;
  onRadioSend: (text: string) => void;
};

let activeTab = 0;
let warrantSubmitting = false;
let currentPhase: AccusationPhaseState | null = null;
let ctx: AccusationArrestContext | null = null;
let handlers: AccusationArrestHandlers | null = null;
let timerId: ReturnType<typeof setInterval> | null = null;
let arrestChatLogEl: HTMLDivElement | null = null;
const arrestChatHistory: { username: string; text: string }[] = [];

function paintArrestChatLog(): void {
  if (!arrestChatLogEl) return;
  arrestChatLogEl.innerHTML = arrestChatHistory
    .map(
      (m) => `<div><b>${escapeHtml(m.username)}</b>: ${escapeHtml(m.text)}</div>`
    )
    .join("");
  arrestChatLogEl.scrollTop = arrestChatLogEl.scrollHeight;
}

function el(id: string): HTMLElement {
  const node = document.getElementById(id);
  if (!node) throw new Error(`#${id} missing`);
  return node;
}

function suspectName(manifest: CaseManifestV1, instanceId: string | null): string {
  if (!instanceId) return "—";
  return npcDisplayName(manifest, instanceId);
}

function statusInput(): ArrestStatusInput | null {
  if (!currentPhase || !ctx) return null;
  return {
    phase: currentPhase,
    selfUserId: ctx.selfUserId,
    players: ctx.players,
    suspectName: (id) => suspectName(ctx!.manifest, id),
  };
}

function stopTimer(): void {
  if (timerId) {
    clearInterval(timerId);
    timerId = null;
  }
}

function updateArrestTimer(): void {
  const node = document.getElementById("accusation-arrest-timer");
  if (!node || !currentPhase?.arrestDeadlineMs) return;
  const sec = Math.max(0, Math.ceil((currentPhase.arrestDeadlineMs - Date.now()) / 1000));
  node.textContent = `${sec}...`;
}

function renderCluesTab(body: HTMLElement): void {
  if (!ctx) return;
  const { manifest, journal, playerNames, minEvidence } = ctx;
  const v = manifest.victim;
  const victimFoundAt = resolveFoundAtDisplay(manifest, v.found_at);
  const list = (journal.discovered_clues as string[]) ?? [];
  const cluesHtml =
    list
      .map((id) => {
        const c = manifest.clues.find((x) => x.clue_id === id);
        const name = clueDisplayName(manifest, id);
        const foundAt = c ? resolveFoundAtDisplay(manifest, c.found_at) : "";
        const related = c ? relatedNpcDisplayName(manifest, c) : null;
        return `<div class="clue-entry">
<p><strong>${escapeHtml(name)}</strong></p>
<p>${escapeHtml(c?.description ?? "")}</p>
<p class="clue-meta" style="font-size:0.85rem;opacity:.85"><strong>Found at</strong> ${escapeHtml(foundAt)}${related ? ` · <strong>Related to</strong> ${escapeHtml(related)}` : ""}</p>
</div>`;
      })
      .join("") || "<p>No clues discovered yet.</p>";

  body.innerHTML = `${arrestTabIntro("clues")}
${renderCaseBriefHtml(manifest, playerNames, { minEvidence })}
<hr style="border-color:#2a3040;margin:16px 0"/>
<h3 style="margin:0 0 8px;font-size:1rem;color:#b8d4ff">Timeline</h3>
<p><strong>Time of death</strong><br/>${escapeHtml(v.time_of_death)}</p>
<p><strong>Found at</strong><br/>${escapeHtml(victimFoundAt)}</p>
<p><strong>Cause</strong><br/>${escapeHtml(v.cause_of_death)}</p>
<h3 style="margin:16px 0 8px;font-size:1rem;color:#b8d4ff">Clues</h3>
${cluesHtml}`;
}

function renderSuspectsTab(body: HTMLElement): void {
  if (!ctx || !currentPhase) return;
  const { manifest, journal, selfUserId } = ctx;
  const metNpc = new Set((journal.talked_to_npcs as string[]) ?? []);
  const metList = manifest.npcs.filter((n) => metNpc.has(n.instance_id));
  const myPick = currentPhase.picks[selfUserId] ?? null;
  const locked = currentPhase.warrantLocked[selfUserId];

  const cards = metList
    .map((n) => {
      const sel = n.instance_id === myPick;
      const npcLabel = npcDisplayName(manifest, n.instance_id);
      const thumb = npcSouthPortraitImgHtml(n.npc_id, { sizePx: 64, alt: npcLabel });
      const persona = npcPersonalityText(n);
      return `<div class="acc-arrest-suspect-card${sel ? " selected" : ""}" data-npc="${escapeHtml(n.instance_id)}" role="button" tabindex="0">
${thumb}
<p class="acc-suspect-name"><strong>${escapeHtml(npcLabel)}</strong></p>
<p class="acc-arrest-persona">${escapeHtml(persona)}</p>
</div>`;
    })
    .join("");

  body.innerHTML = `${arrestTabIntro("suspects")}
<p style="font-size:0.9rem;color:#8892a8">${locked ? "Your warrant is locked — you cannot change your pick." : "Tap a suspect to set your choice."}</p>
<div class="acc-arrest-suspect-list">${cards || "<p>Interview suspects in the world first.</p>"}</div>`;

  if (!locked) {
    body.querySelectorAll(".acc-arrest-suspect-card").forEach((card) => {
      const pick = () => {
        const id = (card as HTMLElement).dataset.npc;
        if (!id || !handlers || !currentPhase || !ctx) return;
        currentPhase.picks[ctx.selfUserId] = id;
        handlers.onPick(id);
        if (activeTab === 1) renderSuspectsTab(body);
      };
      card.addEventListener("click", pick);
      card.addEventListener("keydown", (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          pick();
        }
      });
    });
  }
}

function renderArrestTab(body: HTMLElement): void {
  const h = handlers;
  if (!ctx || !currentPhase || !h) return;
  const { manifest, players, selfUserId } = ctx;
  const input = statusInput();
  const tone = input ? arrestStatusTone(input) : "neutral";
  const toneClass =
    tone === "ready"
      ? "acc-status-ready"
      : tone === "locked"
        ? "acc-status-locked"
        : tone === "warn"
          ? "acc-status-warn"
          : "";
  const statusHtml = input
    ? `<p class="acc-arrest-status ${toneClass}">${arrestDynamicStatus(input)}</p>`
    : "";
  const locked = currentPhase.warrantLocked[selfUserId] || warrantSubmitting;
  const disabledReason = locked
    ? warrantSubmitting
      ? "Submitting your warrant agreement…"
      : ""
    : warrantDisabledReason(input);
  const canWarrant = !disabledReason && !locked;

  const rows = players
    .map((p) => {
      const pickId = currentPhase!.picks[p.userId] ?? null;
      const pickLabel = pickId ? escapeHtml(suspectName(manifest, pickId)) : "—";
      const locked = currentPhase!.warrantLocked[p.userId];
      const thumb = npcSouthPortraitImgHtml(p.characterNpcId, { sizePx: 40, alt: p.displayName });
      const adopt =
        p.userId !== selfUserId && pickId && !currentPhase!.warrantLocked[selfUserId]
          ? `<button type="button" class="secondary acc-adopt-btn" data-from="${escapeHtml(p.userId)}">Pick ${escapeHtml(p.displayName)}'s Suspect</button>`
          : "";
      return `<div class="acc-arrest-player-row">
${thumb}
<div class="acc-arrest-player-meta">
<p><strong>${escapeHtml(p.displayName)}</strong>${locked ? " · locked" : ""}</p>
<p>Suspect: <strong>${pickLabel}</strong></p>
${adopt}
</div>
</div>`;
    })
    .join("");

  body.innerHTML = `${arrestTabIntro("arrest")}
${statusHtml}
<div class="acc-arrest-layout">
<div class="acc-arrest-main">
<div class="acc-arrest-players">${rows}</div>
<button type="button" id="acc-warrant-btn" class="primary" ${canWarrant ? "" : "disabled"}>${locked && warrantSubmitting ? "Locking in…" : locked ? "Warrant locked" : "Agree to Request Warrant"}</button>
<p class="acc-warrant-hint" style="font-size:0.85rem;color:#8892a8;margin-top:8px">${disabledReason ? escapeHtml(disabledReason) : ""}</p>
</div>
<div class="acc-arrest-chat">
<p style="margin:0 0 8px;font-weight:600">Discuss</p>
<div id="acc-arrest-chat-log" class="acc-arrest-chat-log"></div>
<form id="acc-arrest-chat-form" class="acc-arrest-chat-form">
<input type="text" id="acc-arrest-chat-input" maxlength="500" placeholder="Message your team…" autocomplete="off"/>
<button type="submit" class="primary">Send</button>
</form>
</div>
</div>`;

  arrestChatLogEl = document.getElementById("acc-arrest-chat-log") as HTMLDivElement | null;
  paintArrestChatLog();

  document.getElementById("acc-warrant-btn")?.addEventListener("click", () => {
    if (!canWarrant || warrantSubmitting || !currentPhase) return;
    warrantSubmitting = true;
    currentPhase.warrantLocked[ctx.selfUserId] = true;
    h.onWarrant();
    if (activeTab === 2) renderArrestTab(body);
  });
  body.querySelectorAll(".acc-adopt-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      const from = (btn as HTMLButtonElement).dataset.from;
      if (!from || !currentPhase) return;
      const pickId = currentPhase.picks[from];
      if (pickId) {
        currentPhase.picks[ctx.selfUserId] = pickId;
        h.onAdopt(from);
        if (activeTab === 2) renderArrestTab(body);
      }
    });
  });
  const form = document.getElementById("acc-arrest-chat-form");
  form?.addEventListener("submit", (e) => {
    e.preventDefault();
    const inputEl = document.getElementById("acc-arrest-chat-input") as HTMLInputElement | null;
    const text = inputEl?.value.trim();
    if (!text) return;
    h.onRadioSend(text);
    if (inputEl) inputEl.value = "";
  });
}

function render(): void {
  if (!ctx || !currentPhase) return;
  const tabsEl = el("accusation-arrest-tabs");
  const body = el("accusation-arrest-body");
  const instructions = el("accusation-arrest-instructions");
  instructions.innerHTML = arrestInstructionsHeader();

  const ids = ctx.players.map((p) => p.userId);
  const needsSuspect = !currentPhase.picks[ctx.selfUserId];
  const readyForArrest = allPicksMatch(currentPhase, ids);

  tabsEl.innerHTML = TABS.map((t, i) => {
    let hint = "";
    if (i === 1 && needsSuspect) hint = " •";
    if (i === 2 && readyForArrest && !currentPhase.warrantLocked[ctx.selfUserId]) hint = " •";
    return `<div class="tab ${i === activeTab ? "active" : ""}" data-i="${i}">${t}${hint}</div>`;
  }).join("");
  tabsEl.querySelectorAll(".tab").forEach((node) => {
    node.addEventListener("click", () => {
      activeTab = Number((node as HTMLElement).dataset.i);
      render();
    });
  });

  if (activeTab === 0) renderCluesTab(body);
  else if (activeTab === 1) renderSuspectsTab(body);
  else renderArrestTab(body);
}

export function appendArrestChatMessage(username: string, text: string): void {
  arrestChatHistory.push({ username, text });
  if (!arrestChatLogEl) return;
  paintArrestChatLog();
}

export function isAccusationArrestOverlayOpen(): boolean {
  return document.getElementById("accusation-arrest-overlay")?.classList.contains("open") ?? false;
}

export function openAccusationArrestOverlay(
  phase: AccusationPhaseState,
  context: AccusationArrestContext,
  h: AccusationArrestHandlers
): void {
  currentPhase = phase;
  ctx = context;
  handlers = h;
  activeTab = 0;
  warrantSubmitting = false;
  const overlay = el("accusation-arrest-overlay");
  overlay.classList.add("open");
  setPlayerOverlayOpen("accusation-arrest", true);
  render();
  updateArrestTimer();
  stopTimer();
  timerId = setInterval(updateArrestTimer, 1000);
}

export function syncAccusationArrestOverlay(phase: AccusationPhaseState): void {
  currentPhase = mergeAccusationPhaseFromSync(
    phase,
    currentPhase,
    ctx?.selfUserId ?? ""
  );
  if (!isAccusationArrestOverlayOpen() || !ctx) return;
  if (ctx.selfUserId in phase.warrantLocked && phase.warrantLocked[ctx.selfUserId]) {
    warrantSubmitting = false;
  }
  const ids = ctx.players.map((p) => p.userId);
  if (allPicksMatch(phase, ids) && activeTab < 2) {
    activeTab = 2;
  }
  render();
  updateArrestTimer();
}

export function closeAccusationArrestOverlay(): void {
  stopTimer();
  currentPhase = null;
  ctx = null;
  handlers = null;
  warrantSubmitting = false;
  activeTab = 0;
  arrestChatLogEl = null;
  arrestChatHistory.length = 0;
  const overlay = document.getElementById("accusation-arrest-overlay");
  overlay?.classList.remove("open");
  setPlayerOverlayOpen("accusation-arrest", false);
}
